import { Center, Group, MantineTheme, Paper, Stack, Text, useMantineTheme } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { LuSignal, LuSignalHigh, LuSignalLow, LuSignalMedium, LuSignalZero } from 'react-icons/lu'
import { TbBattery, TbBattery1, TbBattery2, TbBattery3, TbBattery4, TbStackPop } from 'react-icons/tb'

import { useLocalOrProxiedControlUnitClient } from '@/api-proxy/useProxiedClient'
import { getBatteryOptions } from '@/api/controlUnitApi'
import { getSystemStatusOptions } from '@/api/navigationApi'
import { Client } from '@/api/main/client'
import { SystemStatusResponse } from '@/api/navigationApi/types.gen'
import RippleIcon from '@/components/RippleIcon'
import { VEHICLE_HEALTH_BATTERY_REFETCH_INTERVAL_MS, VEHICLE_HEALTH_STATUS_REFETCH_INTERVAL_MS } from '@/constants'
import { extendKey } from '@/utils/queryKey'

const CAMERA_POSITIONS: (keyof SystemStatusResponse['cameras'])[] = ['FL', 'FC', 'FR', 'RL', 'RR']

type LevelIncrement = 0 | 1 | 2 | 3 | 4

// Maps a 0-1 level onto five increments (empty -> full)
export const determineLevelIncrement = (level: number): LevelIncrement => {
  if (level < 0.125) return 0
  if (level < 0.375) return 1
  if (level < 0.625) return 2
  if (level < 0.875) return 3
  return 4
}

const BATTERY_ICONS: Record<LevelIncrement, IconType> = {
  0: TbBattery,
  1: TbBattery1,
  2: TbBattery2,
  3: TbBattery3,
  4: TbBattery4,
}

const SIGNAL_ICONS: Record<LevelIncrement, IconType> = {
  0: LuSignalZero,
  1: LuSignalLow,
  2: LuSignalMedium,
  3: LuSignalHigh,
  4: LuSignal,
}

type LevelPalette = Record<LevelIncrement, keyof MantineTheme['colors']>

// Red -> green as the level rises; the no-reading state is colored separately
const LEVEL_PALETTE: LevelPalette = { 0: 'red', 1: 'orange', 2: 'yellow', 3: 'lime', 4: 'green' }

const ICON_SLOT_HEIGHT = 26

// Shared column layout so the icons of all sections sit on one row and the values on another
const Indicator: FC<{
  icon: ReactNode
  value: string
  label: string
  dimmedValue?: boolean
}> = ({ icon, value, label, dimmedValue }) => (
  <Stack gap={2} align="center" title={label}>
    <Center h={ICON_SLOT_HEIGHT}>{icon}</Center>
    <Text size="xs" fw={500} lh={1} ta="center" c={dimmedValue ? 'dimmed' : undefined}>
      {value}
    </Text>
  </Stack>
)

const LevelIndicator: FC<{
  level: number | null | undefined
  icons: Record<LevelIncrement, IconType>
  label: string
}> = ({ level, icons, label }) => {
  const theme = useMantineTheme()

  const hasLevel = level !== null && level !== undefined
  const increment = hasLevel ? determineLevelIncrement(level) : 0
  const color = hasLevel ? theme.colors[LEVEL_PALETTE[increment]][7] : 'black'
  const Icon = icons[increment]

  return (
    <Indicator
      icon={<Icon size={26} color={color} aria-label={label} />}
      value={hasLevel ? `${Math.round(level * 100)}%` : '—'}
      label={label}
    />
  )
}

const VehicleStatus: FC<{ client: Client | undefined; vehicleId: string | undefined }> = ({ client, vehicleId }) => {
  const { t } = useTranslation('navigation')
  const theme = useMantineTheme()
  const controlUnitClient = useLocalOrProxiedControlUnitClient(vehicleId ?? null)

  // The proxy base URL is identical for every vehicle (the vehicle is a request
  // header), so the generated query keys must be extended to avoid serving
  // another vehicle's cached data after a switch
  const { data: battery } = useQuery({
    ...extendKey(getBatteryOptions({ client: controlUnitClient }), vehicleId ?? 'local'),
    refetchInterval: VEHICLE_HEALTH_BATTERY_REFETCH_INTERVAL_MS,
    retry: 1,
  })

  const { data: systemStatus } = useQuery({
    ...extendKey(getSystemStatusOptions({ client }), vehicleId ?? 'local'),
    refetchInterval: VEHICLE_HEALTH_STATUS_REFETCH_INTERVAL_MS,
    retry: 1,
  })

  const getCameraColor = (enabled: boolean | undefined) => {
    if (enabled === undefined) return theme.colors.gray[5]
    return enabled ? theme.colors.green[7] : theme.colors.gray[5]
  }

  const queueTotal = systemStatus
    ? Object.values(systemStatus.queues).reduce((total, count) => total + count, 0)
    : undefined

  return (
    <Paper p="xs" shadow="xs" withBorder>
      <Group gap="lg" wrap="nowrap" align="center">
        <LevelIndicator level={battery?.battery_percentage} icons={BATTERY_ICONS} label={t('battery')} />
        <LevelIndicator level={systemStatus?.gps_quality} icons={SIGNAL_ICONS} label={t('gpsSignal')} />
        <Indicator
          icon={
            <TbStackPop
              size={24}
              aria-label={t('uploadQueue')}
              color={queueTotal === undefined ? theme.colors.gray[5] : undefined}
            />
          }
          value={`${queueTotal ?? '—'}`}
          label={t('uploadQueue')}
        />
        <Group gap={4} wrap="nowrap" align="flex-start" title={t('cameras')}>
          {CAMERA_POSITIONS.map(position => (
            <Indicator
              key={position}
              icon={<RippleIcon color={getCameraColor(systemStatus?.cameras[position])} size={18} />}
              value={position}
              label={position}
            />
          ))}
        </Group>
      </Group>
    </Paper>
  )
}

export default VehicleStatus
